import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CaretRight, CreditCard, EnvelopeSimple, Phone, WarningCircle } from 'phosphor-react';
import { SButton } from '@/components/ui/atoms/SButton';
import SDialog from '@/components/ui/molecules/SDialog';
import { useUserLocation } from '@/hooks/useUserLocation';
import { useUpdatePaymentLink } from '@/hooks/useUpdatePaymentLink';
import './PastDueGate.css';

const GENERAL_SUPPORT_EMAIL = 'support@tbx.golf';

/**
 * Default country code applied when a Box Pro record has a mobile but no
 * countryCode — matches the API's resolveLocationContact (location-contact.util.ts),
 * so the tel: link here dials the same number as the check-in "Contact us" link.
 */
const DEFAULT_COUNTRY_CODE = '+1';

/** Digits-only (plus leading +) so the tel: href is actually dialable. */
function toTelHref(countryCode: string | undefined, mobile: string): string {
  return `tel:${`${countryCode ?? ''}${mobile}`.replace(/[^\d+]/g, '')}`;
}

/** Pretty-print NANP numbers; anything else is shown as stored. */
function formatPhone(countryCode: string | undefined, mobile: string): string {
  const raw = `${countryCode ?? ''}${mobile}`.replace(/[^\d+]/g, '');
  const nanp = raw.match(/^\+?1(\d{3})(\d{3})(\d{4})$/) ?? raw.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (nanp) return `+1 (${nanp[1]}) ${nanp[2]}-${nanp[3]}`;
  return countryCode ? `${countryCode} ${mobile}` : mobile;
}

interface ContactRowProps {
  readonly href: string;
  readonly label: string;
  readonly value: string;
  readonly icon: React.ReactNode;
}

const ContactRow: React.FC<ContactRowProps> = ({ href, label, value, icon }) => (
  <a className="past-due-gate__contact" href={href}>
    <span className="past-due-gate__contact-icon">{icon}</span>
    <span className="past-due-gate__contact-body">
      <span className="past-due-gate__contact-label">{label}</span>
      <span className="past-due-gate__contact-value">{value}</span>
    </span>
    <CaretRight size={16} weight="bold" className="past-due-gate__contact-caret" />
  </a>
);

/**
 * Full-screen takeover shown while a membership is `past_due`. Everything else
 * on home is hidden — the only action available is updating the card.
 */
export const PastDueGate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { location } = useUserLocation();

  /**
   * The club's hosted billing page, if it has one.
   *
   * When present, this gate is the whole flow: the CTA confirms and
   * hands the member straight out to the billing page. The in-app
   * `/membership/update-payment` screen is only for clubs still on
   * the Stripe form, so routing everyone through it would have meant
   * a screen whose only content was another button.
   */
  const { data: paymentLink } = useUpdatePaymentLink();
  const billingUrl = paymentLink?.updatePaymentLink;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleCta = () => {
    if (billingUrl) {
      setConfirmOpen(true);
      return;
    }
    navigate('/membership/update-payment');
  };

  /* Same outbound-link call as everywhere else in the app — the
     native wrapper hands any non-app origin to the system browser. */
  const openBilling = () => {
    setConfirmOpen(false);
    if (billingUrl) window.open(billingUrl, '_blank', 'noopener,noreferrer');
  };

  // Prefer the Box Pro's own mobile; fall back to the location's public number.
  const boxPro = location?.boxPro;
  const phone = boxPro?.mobile || location?.defaultPhoneNumber;
  // defaultPhoneNumber is stored fully qualified, so only the Box Pro's mobile
  // gets a country code (defaulted, same as the API-side resolver).
  const phoneCountryCode = boxPro?.mobile
    ? boxPro?.countryCode || DEFAULT_COUNTRY_CODE
    : undefined;
  const email = boxPro?.email;
  const proName = [boxPro?.firstName, boxPro?.lastName].filter(Boolean).join(' ');

  return (
    <div className="past-due-gate">
      <div className="past-due-gate__icon">
        <WarningCircle size={36} weight="fill" />
      </div>

      <h1 className="past-due-gate__title">{t('membership.pastDue.title')}</h1>
      <p className="past-due-gate__message">{t('membership.pastDue.message')}</p>

      <div className="past-due-gate__card">
        <p className="past-due-gate__card-text">{t('membership.pastDue.helper')}</p>
      </div>

      <SButton
        size="xl"
        variant="primary"
        className="past-due-gate__cta"
        icon={<CreditCard size={20} weight="fill" />}
        label={t('membership.pastDue.cta')}
        onClick={handleCta}
      />

      <div className="past-due-gate__support">
        <p className="past-due-gate__support-title">
          {t('membership.pastDue.support.title')}
        </p>
        <p className="past-due-gate__support-subtitle">
          {proName
            ? t('membership.pastDue.support.subtitleNamed', { name: proName })
            : t('membership.pastDue.support.subtitle')}
        </p>

        {(phone || email) && (
          <div className="past-due-gate__contacts">
            {phone && (
              <ContactRow
                href={toTelHref(phoneCountryCode, phone)}
                label={t('membership.pastDue.support.call')}
                value={formatPhone(phoneCountryCode, phone)}
                icon={<Phone size={18} weight="fill" />}
              />
            )}
            {email && (
              <ContactRow
                href={`mailto:${email}`}
                label={t('membership.pastDue.support.email')}
                value={email}
                icon={<EnvelopeSimple size={18} weight="fill" />}
              />
            )}
          </div>
        )}

        <p className="past-due-gate__support-general">
          {t('membership.pastDue.support.general')}{' '}
          <a className="past-due-gate__link" href={`mailto:${GENERAL_SUPPORT_EMAIL}`}>
            {GENERAL_SUPPORT_EMAIL}
          </a>
        </p>
      </div>

      {/* Sets expectations before the member leaves the app: where
          they are going, and that access comes back on a delay rather
          than the instant they pay. Without it, returning to a still
          -blocked app reads as the payment having failed. */}
      <SDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t('membership.pastDue.confirm.title')}
        onConfirm={openBilling}
        confirmText={t('membership.pastDue.confirm.confirm')}
        cancelText={t('membership.pastDue.confirm.cancel')}
      >
        <p className="past-due-gate__dialog-text">
          {t('membership.pastDue.confirm.body')}
        </p>
      </SDialog>
    </div>
  );
};

export default PastDueGate;
